import { ReadonlyVec3, vec3 } from 'gl-matrix';

/** Convert centimeters to millimeters. */
export function cmToMm(cm: number): number {
    return cm * 10;
}

/** Convert millimeters to centimeters. */
export function mmToCm(mm: number): number {
    return mm / 10;
}

/** Convert square centimeters to square millimeters (1 cm² = 100 mm²). */
export function cm2ToMm2(areaCm2: number): number {
    return areaCm2 * 100;
}

/** Convert square millimeters to square centimeters (1 cm² = 100 mm²). */
export function mm2ToCm2(areaMm2: number): number {
    return areaMm2 / 100;
}

/** Convert cubic millimeters to cubic centimeters (1 cm³ = 1000 mm³). */
export function mm3ToCm3(volumeMm3: number): number {
    return volumeMm3 / 1000;
}

/**
 * House display precision for clinical volumes: 2 decimals in cm³, as
 * the structure properties panel has always shown them
 * (`StructureDetailsInformation`). Exported so a second volume readout
 * cannot quietly pick a different number of decimals.
 */
export const VOLUME_CM3_DECIMALS = 2;

/**
 * Decimal places used for BOTH displaying a density override and rounding
 * it on commit. A single source of truth is what keeps the value shown,
 * stored, and exported to the RTSTRUCT identical — no "shows 1.050,
 * exports 1.0500001" divergence.
 *
 * Three decimals is deliberate: CT-to-density calibration does not resolve
 * anywhere near the 4th decimal, so it is noise. It also bounds the
 * exported DICOM Decimal String — a value in `[0, 20]` rounded to 3
 * decimals is at most 6 characters (`"20.000"`), well within the 16-byte
 * DS limit (PS3.5 §6.2) — so no separate length check is needed on the
 * write path.
 */
export const DENSITY_OVERRIDE_DECIMALS = 3;

/**
 * The smallest volume {@link VOLUME_CM3_DECIMALS} can state, in cm³.
 * Anything non-zero below it is reported as "below this", never as zero.
 */
const VOLUME_CM3_SMALLEST_STATEABLE = 0.01;

/**
 * Format a domain volume (mm³) for display in cm³ — the UI-boundary
 * conversion, at the house precision. The unit belongs in the label, so
 * this returns the number only.
 *
 * A volume that is genuinely zero formats as "0.00". A NON-ZERO volume
 * too small for the house precision formats as the BOUNDED statement
 * "< 0.01" rather than rounding to "0.00": a readout whose whole purpose
 * is to confirm that something was found must never report a found
 * structure as nothing, and stating a bound is the honest way to say
 * "smaller than this display can express" without inventing decimals the
 * neighbouring volumes do not carry (docs/ui_specs.md — never silently
 * "fix" a clinical value; state it).
 *
 * @param volumeMm3 Volume in cubic millimetres (domain unit).
 * @returns e.g. "12.35", "0.00", "< 0.01".
 */
export function formatMm3AsCm3(volumeMm3: number): string {
    return formatCm3(mm3ToCm3(volumeMm3), volumeMm3 > 0);
}

/**
 * Format a volume ALREADY in cm³ at the house precision — for a readout
 * whose value never passed through mm³ (the smoothing panel's
 * before → after, read from the ROI's own cm³ volume). Same precision
 * and same underflow convention as {@link formatMm3AsCm3}, so two
 * volumes shown side by side in the UI cannot be stated to different
 * numbers of decimals.
 *
 * @param volumeCm3 Volume in cm³
 * @param isNonZero Whether the underlying volume is genuinely non-zero.
 *        Defaults to asking the cm³ value itself, which is right
 *        whenever cm³ is where the volume actually comes from; a caller
 *        holding a finer-grained source passes that source's verdict so
 *        a volume that underflows the conversion is still reported as
 *        "< 0.01" rather than as nothing.
 * @returns e.g. "12.35", "0.00", "< 0.01".
 */
export function formatCm3(
    volumeCm3: number,
    isNonZero = volumeCm3 > 0
): string {
    if (
        // Asked of the DOMAIN value: "did we find anything" is a fact
        // about the volume, not about the number the conversion
        // produced (a small enough mm³ underflows to 0 cm³).
        isNonZero &&
        // Round FIRST, then compare: 0.00999 rounds up to 0.01 and is
        // perfectly stateable, while 0.004 does not and is not.
        Number(volumeCm3.toFixed(VOLUME_CM3_DECIMALS)) <
            VOLUME_CM3_SMALLEST_STATEABLE
    ) {
        return `< ${VOLUME_CM3_SMALLEST_STATEABLE.toFixed(
            VOLUME_CM3_DECIMALS
        )}`;
    }
    return volumeCm3.toFixed(VOLUME_CM3_DECIMALS);
}

/**
 * Format a domain area (mm²) for display in cm², at the precision and with
 * the underflow convention of {@link formatMm3AsCm3}: e.g. "3.14", "< 0.01".
 */
export function formatMm2AsCm2(areaMm2: number): string {
    return formatCm3(mm2ToCm2(areaMm2), areaMm2 > 0);
}

/** Patient axis of a value or overlay element (DICOM LPS X/Y/Z). */
export type AxisKey = 'x' | 'y' | 'z';

/**
 * The patient axis (0 = X, 1 = Y, 2 = Z) a 3D direction vector
 * dominantly runs along. For an axis-aligned unit vector this is simply
 * its non-zero component; ties resolve to the earliest axis, so a
 * diagonal gives a deterministic answer rather than an arbitrary one.
 *
 * @throws if the direction vector is zero-length — a collapsed direction
 *   has no axis, and silently naming one would attribute a value to the
 *   wrong anatomy.
 */
export function getDominantAxisIndex(direction: ReadonlyVec3): 0 | 1 | 2 {
    const x = Math.abs(direction[0]);
    const y = Math.abs(direction[1]);
    const z = Math.abs(direction[2]);
    if (x === 0 && y === 0 && z === 0) {
        throw new Error(
            'zero-length direction vector: there is no dominant axis'
        );
    }
    if (x >= y && x >= z) {
        return 0;
    }
    return y >= z ? 1 : 2;
}

/**
 * Returns the sign (+1 or -1) of the dominant axis component of a
 * 3D direction vector. For axis-aligned unit vectors
 * (e.g. [0, 0, -1]), this returns the sign of the non-zero
 * component.
 *
 * Used to convert a dot-product projection (planeCoordinates) back
 * to the patient-space coordinate along the through-plane axis.
 *
 * @throws if the direction vector is zero-length.
 */
export function getDominantAxisSign(direction: ReadonlyVec3): 1 | -1 {
    return direction[getDominantAxisIndex(direction)] >= 0 ? 1 : -1;
}

/**
 * Where the plane through `point` with normal `normal` lies: its offset in mm
 * along the normal, signed by the patient axis the normal lies nearest to,
 * and that axis.
 *
 * @throws if the normal is zero-length.
 */
export function planePositionAlong(
    point: ReadonlyVec3,
    normal: ReadonlyVec3
): { axis: 'X' | 'Y' | 'Z'; offsetMm: number } {
    return {
        axis: (['X', 'Y', 'Z'] as const)[getDominantAxisIndex(normal)],
        offsetMm: getDominantAxisSign(normal) * vec3.dot(point, normal),
    };
}

/**
 * Converts a patient coordinate (mm) to cm rounded to 0.01 cm, the precision
 * the coordinate readouts show. Rounds the count of 0.1 mm half up, so every
 * tie rounds the same way and a value that rounds to zero carries no minus
 * sign once formatted.
 */
export function roundMmToHundredthCm(valueMm: number): number {
    return Math.round(valueMm * 10) / 100;
}

/**
 * Calculate the normalized orientation value for a plane coordinate.
 * Converts planeCoordinates (mm, projected along view direction) to
 * the patient-space coordinate (cm) for display.
 *
 * @param planeCoordinates - dot(intersectionPoint, viewDirection) in mm
 * @param viewDirectionSign - sign of the dominant axis component of the
 *   view direction vector (+1 or -1). This corrects the projection so
 *   that the displayed coordinate matches the patient axis regardless
 *   of whether the view direction is positive or negative along that axis.
 * @returns cm to 2 decimals, rounded by {@link roundMmToHundredthCm} as a
 *   point's coordinate fields are, so the same position reads the same in both.
 */
export function calculateNormalizedOrientationValue(
    planeCoordinates: number,
    viewDirectionSign: number = -1
): string {
    return roundMmToHundredthCm(planeCoordinates * viewDirectionSign).toFixed(
        2
    );
}

/**
 * Formats a value already in centimeters as a display string with
 * fixed decimal places.
 * @param valueCm - The value in centimeters.
 * @param decimalPlaces - Number of decimal places (default: 2).
 * @returns Formatted string, e.g. "1.23".
 */
export function formatCm(valueCm: number, decimalPlaces: number = 2): string {
    return valueCm.toFixed(decimalPlaces);
}

/**
 * Converts an internal mm value to centimeters and formats it as a
 * display string with fixed decimal places.
 *
 * Use 3 decimal places for voxel-scale distances (pixel spacing,
 * slice thickness) and 2 for macro distances (field of view,
 * physical extent). See docs/ui_specs.md for the full convention.
 *
 * @param valueMm - The value in millimeters (domain units).
 * @param decimalPlaces - Number of decimal places (default: 2).
 * @returns Formatted cm string, e.g. "0.098" or "25.60".
 */
export function formatMmAsCm(
    valueMm: number,
    decimalPlaces: number = 2
): string {
    return formatCm(mmToCm(valueMm), decimalPlaces);
}

/**
 * The two display precisions of a distance (docs/ui_specs.md): `voxel` for
 * pixel spacing, slice thickness and slice spacing, where sub-millimetre
 * precision is actionable; `macro` for a field of view or an extent.
 */
export type DistanceScale = 'voxel' | 'macro';

const DISTANCE_SCALE_DECIMALS: Record<DistanceScale, number> = {
    voxel: 3,
    macro: 2,
};

/**
 * {@link formatMmAsCm} at the house precision of the distance's scale, so
 * two readouts of one quantity cannot state it to different decimals.
 *
 * @returns e.g. "0.300" for a voxel-scale, "25.60" for a macro distance.
 */
export function formatMmAsCmAtScale(
    valueMm: number,
    scale: DistanceScale
): string {
    return formatMmAsCm(valueMm, DISTANCE_SCALE_DECIMALS[scale]);
}
